import React, { useState, useEffect } from "react";
import { PageHeader } from "components/ui";
import {
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import GriStandardsSelector from "components/customizedStandardsComponent/GriStandardsSelector";
import GriFormComponent from "components/customizedStandardsComponent/GriFormComponent";
import UploadEvidenceComponent from "components/customizedStandardsComponent/UploadEvidenceComponent";
import DownloadReport from "components/customizedStandardsComponent/DownloadReport";
import DownloadPostReviewGapForm from "components/customizedStandardsComponent/DownloadPostReviewGapForm";
import UploadPostReviewGapForm from "components/customizedStandardsComponent/UploadPostReviewGapForm";
import DownloadPostReviewReports from "components/customizedStandardsComponent/DownloadPostReviewReports";

import StandardsSelector from "components/customizedStandardsComponent/GriStandardsSelector";
import NavigationButtons from "helper/navigation/NavigationButtons";
import ReusableFormComponent from "components/customizedStandardsComponent/EsgFormComponent";
import {
  ENV_SOCIAL_MANAGEMENT_PLANS,
  ESG_ANALYTIC_SUBJECT,
  ESG_AI_DRIVEN_PROCESS_OUTPUT_GEN,
} from "data/standards/griStandards";
import { ESG_ANALYTIC_FORM, ESG_BENCHMARKING } from "data/forms/griFormSchema";
import {
  benchMarkingCategory,
  preferredBenchmarkOptions,
  benchmarkingInsightsDownload,
} from "data/reports/relevantReports";
import ReportsReviewDialog from "components/customizedStandardsComponent/ReportsReviewDialog";
import UploadEvidenceReviewReports from "components/customizedStandardsComponent/UploadEvidenceReviewReports";

const EsgBenchMarkingStepper = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState(0);
  const [reportData, setReportData] = useState({
    selectedStandards: [],
    companyInfo: {},
  });
  const [showReviewPopup, setShowReviewPopup] = useState(false);

  const steps = [
    "Select ESG BenchMarking Standards",
    "Select Preferred Benchmark Options",
    "Fill ESG Analytics Form",
    "Upload Evidence",
    "Review and Comment on ESG BenchMarking",
    "Upload Evidence and Review Reports",
    "Download Post Review Gap Form",
    "Upload Post Review Gap Form",
    "Download Reports",
  ];

  useEffect(() => {
    if (activeStep === 5 && prevStep === 4) {
      setShowReviewPopup(true);
    }
  }, [activeStep, prevStep]);

  const handleNext = () => {
    setPrevStep(activeStep);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setPrevStep(activeStep);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSaveStandards = (selectedStandards) => {
    setReportData((prev) => ({
      ...prev,
      selectedStandards,
    }));
    console.log("Standards saved:", selectedStandards);
  };

  const handleClosePopup = () => {
    setShowReviewPopup(false);
  };

  const handleSkipToStep5 = () => {
    setPrevStep(activeStep);
    setActiveStep(6);
    setShowReviewPopup(false);
  };

  const COMPLIANCE_EVIDENCE_TYPES = [
    "Upload annual ESG Vision/ESG report",
    "Governance metrics (board diversity, ethics policy, etc.)",
    "Environmental metrics (GHG emissions, energy, water, waste)",
    "Social metrics (workforce diversity, DEI, labor standards)",
  ];

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <DownloadReport
            key="bench-marking-category-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={benchMarkingCategory}
            title="Benchmarking category"
            ReportDownloadButtonShow={false}
          />
        );
      case 1:
        return (
          <DownloadReport
            key="environmental-social-system-step"
            onPrevious={handleBack}
            ReportDownloadButtonShow={false}
            title="Select the Preferred Benchmark"
            onNext={handleNext}
            initialItems={preferredBenchmarkOptions}
          />
        );
      case 2:
        return (
          <ReusableFormComponent
            {...ESG_BENCHMARKING}
            onNext={handleNext}
            onPrevious={handleBack}
            showPreviousButton={true}
          />
        );
      case 3:
        return (
          <UploadEvidenceComponent
            onPrevious={handleBack}
            onNext={handleNext}
            evidenceTypes={COMPLIANCE_EVIDENCE_TYPES}
          />
        );
      case 4:
        return (
          <DownloadReport
            key="benchmarking-insights-download-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={benchmarkingInsightsDownload}
            showReportCountdownButton={true}
          />
        );
      case 5:
        return (
          <UploadEvidenceReviewReports
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 6:
        return (
          <DownloadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 7:
        return (
          <UploadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 8:
        return (
          <DownloadPostReviewReports
            onPrevious={handleBack}
            standardName="ESG Benchmarking"
            showReportCountdownButton={true}
          />
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PageHeader title="AI-Powered ESG Reporting across Global, National, Sectoral, and Industry-Specific Standards" />
          </Box>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <NavigationButtons
                imageKey="dashboardImg5_1"
                title={"ESG Benchmarking"}
                activeStep={activeStep}
                onBack={handleBack}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          {getStepContent(activeStep)}
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 4,
              mb: 2,
            }}
          ></Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: -1,
            }}
          >
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: index <= activeStep ? "#02462E" : "#e0e0e0",
                  mx: 1,
                }}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      <ReportsReviewDialog
        open={showReviewPopup}
        onClose={handleClosePopup}
        onSkip={handleSkipToStep5}
      />
    </>
  );
};

export default EsgBenchMarkingStepper;
